// Modelos LLM (Admin) — markup LITERAL do wireframe §14 (varredura v2
// §5.6-C3): .dhead (busca + filtros provedor/auth/reasoning + "Novo modelo")
// + acordeon por provedor (<details class="mprov">, primeiro aberto) + cards
// .modc clicáveis com kebab ⋮ → modais criar/editar/excluir (WfModalShell).
// CSS: wf-models.css (+ wf-agents.css chrome, wf-skills.css kebab).
// C1: custos pt-BR via fmtCost → fmtMoneyUSD (no ModelCard).
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type LlmModel, type LlmProvider } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { ModelCard } from '@/components/models/ModelCard';
import { ModelEditDialog } from '@/components/models/ModelEditDialog';
import { ModelCreateDialog } from '@/components/models/ModelCreateDialog';
import { ModelDeleteDialog } from '@/components/models/ModelDeleteDialog';
import { authFlags, hasReasoning } from '@/components/models/modelHelpers';
import { Cpu, Plus, Search } from 'lucide-react';
import '@/styles/wf-agents.css'; // chrome compartilhado: .dhead/.dfilters/.search/.dnewbtn
import '@/styles/wf-skills.css'; // kebab .skl-kebab/.skl-menu
import '@/styles/wf-models.css';

type AuthFilter = '' | 'oauth' | 'api_key';
type ReasoningFilter = '' | 'with' | 'without';

interface ProviderGroup {
  provider: LlmProvider;
  models: LlmModel[];
}

export function ModelsCatalogPage() {
  const [search, setSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [authFilter, setAuthFilter] = useState<AuthFilter>('');
  const [reasoningFilter, setReasoningFilter] = useState<ReasoningFilter>('');

  const [editing, setEditing] = useState<LlmModel | null>(null);
  const [deleting, setDeleting] = useState<LlmModel | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  // Acordeon controlado: null = default (primeiro provedor expandido, como no wireframe).
  const [expanded, setExpanded] = useState<string[] | null>(null);

  const { data: providersData, isLoading: loadingProviders } = useQuery({
    queryKey: ['llm-catalog', 'providers'],
    queryFn: () => api.listLlmProviders(),
    staleTime: 60 * 1000,
  });

  const { data: modelsData, isLoading: loadingModels } = useQuery({
    queryKey: ['llm-catalog', 'models'],
    queryFn: () => api.listLlmModels({ page_size: 200 }),
    staleTime: 60 * 1000,
  });

  const providers = useMemo(() => providersData ?? [], [providersData]);
  const providerById = useMemo(
    () => new Map(providers.map((p) => [p.id, p])),
    [providers]
  );

  const groups = useMemo<ProviderGroup[]>(() => {
    const all = modelsData?.models ?? [];
    const q = search.trim().toLowerCase();

    const visible = all.filter((m) => {
      // Soft-deleted (deprecated) sai da listagem, como descrito no modal de exclusão.
      if (m.status === 'deprecated') return false;
      if (providerFilter && m.provider_id !== providerFilter) return false;
      const flags = authFlags(m);
      if (authFilter === 'oauth' && !flags.oauth) return false;
      if (authFilter === 'api_key' && !flags.apiKey) return false;
      const reasoning = hasReasoning(m);
      if (reasoningFilter === 'with' && !reasoning) return false;
      if (reasoningFilter === 'without' && reasoning) return false;
      if (q && !m.model_key.toLowerCase().includes(q) && !m.display_name.toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });

    const byProvider = new Map<string, LlmModel[]>();
    for (const m of visible) {
      const list = byProvider.get(m.provider_id) ?? [];
      list.push(m);
      byProvider.set(m.provider_id, list);
    }

    const result: ProviderGroup[] = [];
    // Mantém a ordem dos provedores da API; modelos sem provedor conhecido vão pro fim.
    for (const p of providers) {
      const models = byProvider.get(p.id);
      if (models?.length) {
        result.push({ provider: p, models });
        byProvider.delete(p.id);
      }
    }
    for (const [providerId, models] of byProvider) {
      result.push({
        provider:
          providerById.get(providerId) ??
          ({
            id: providerId,
            key: providerId,
            display_name: 'Provedor desconhecido',
            site_url: null,
            supported_auth_methods: [],
            canonical_top_model_key: null,
            config: {},
            status: 'active',
          } as LlmProvider),
        models,
      });
    }
    return result;
  }, [modelsData, providers, providerById, search, providerFilter, authFilter, reasoningFilter]);

  const totalVisible = groups.reduce((acc, g) => acc + g.models.length, 0);
  const isLoading = loadingProviders || loadingModels;
  const expandedValue = expanded ?? (groups[0] ? [groups[0].provider.id] : []);
  const editingProviderName = editing
    ? providerById.get(editing.provider_id)?.display_name ?? '—'
    : '—';

  const toggleGroup = (providerId: string) => {
    setExpanded(
      expandedValue.includes(providerId)
        ? expandedValue.filter((id) => id !== providerId)
        : [...expandedValue, providerId],
    );
  };

  return (
    <div>
      <PageHeader
        title="Modelos LLM"
        description={`${totalVisible} modelos no catálogo, agrupados por provedor`}
      />

      {/* Filtros + Novo modelo (wireframe §14 .dhead) */}
      <div className="dhead" style={{ marginTop: 14 }}>
        <div className="dfilters">
          <div className="search" style={{ flex: 1, minWidth: 170 }}>
            <Search className="ic" aria-hidden />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar modelo"
              aria-label="Buscar modelo"
            />
          </div>
          <select
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            aria-label="Filtro de provedor"
          >
            <option value="">Provedor: todos</option>
            {providers.map((p) => (
              <option key={p.id} value={p.id}>{p.display_name}</option>
            ))}
          </select>
          <select
            value={authFilter}
            onChange={(e) => setAuthFilter(e.target.value as AuthFilter)}
            aria-label="Filtro de auth"
          >
            <option value="">Auth: todos</option>
            <option value="oauth">OAuth</option>
            <option value="api_key">API Key</option>
          </select>
          <select
            value={reasoningFilter}
            onChange={(e) => setReasoningFilter(e.target.value as ReasoningFilter)}
            aria-label="Filtro de reasoning"
          >
            <option value="">Reasoning: todos</option>
            <option value="with">com reasoning</option>
            <option value="without">sem reasoning</option>
          </select>
        </div>
        <button type="button" className="dnewbtn" onClick={() => setCreateOpen(true)}>
          <Plus className="ic" aria-hidden />
          Novo modelo
        </button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
        </div>
      ) : groups.length === 0 ? (
        <EmptyState
          icon={Cpu}
          title="Nenhum modelo"
          description="Tente limpar os filtros ou cadastre um modelo novo."
        />
      ) : (
        groups.map(({ provider, models }) => {
          const isOpen = expandedValue.includes(provider.id);
          return (
            // Acordeon por provedor — <details class="mprov"> literal do wireframe.
            <details key={provider.id} className="mprov" open={isOpen}>
              <summary
                onClick={(e) => {
                  e.preventDefault();
                  toggleGroup(provider.id);
                }}
              >
                <Cpu className="ic" aria-hidden />
                {provider.display_name}
                <span className="mp-count">
                  {models.length} {models.length === 1 ? 'modelo' : 'modelos'}
                </span>
                <span className="chev">▸</span>
              </summary>
              <div className="mprov-body">
                {models.map((m) => (
                  <ModelCard key={m.id} model={m} onEdit={setEditing} onDelete={setDeleting} />
                ))}
              </div>
            </details>
          );
        })
      )}

      <p style={{ fontSize: '.76rem', color: 'var(--wf-muted)', marginTop: 14 }}>
        <b style={{ color: 'var(--wf-text)' }}>Custo por 1M tokens.</b> Valores vêm da tabela{' '}
        <code>llm_models</code>.
      </p>

      {/* Modais */}
      <ModelEditDialog
        model={editing}
        providerName={editingProviderName}
        onClose={() => setEditing(null)}
      />
      <ModelDeleteDialog model={deleting} onClose={() => setDeleting(null)} />
      {/* key remonta o modal a cada abertura → form sempre fresco (sem setState em effect) */}
      <ModelCreateDialog
        key={createOpen ? 'create-open' : 'create-closed'}
        open={createOpen}
        providers={providers}
        initialProviderId={providerFilter || undefined}
        onClose={() => setCreateOpen(false)}
      />
    </div>
  );
}
